import { useState, useEffect, useRef } from 'react';
import { useConnectionStore } from '@ewf/stores/connection';
import { MARKETPLACE_CHAINS } from '@shared/types/enums';
import { type Wallet } from '@ewf/components/ModalUI/WalletSelector';
import { ConnectWC } from '@ewf/components/ConnectWC';
import { ConnectionLoading } from '@ewf/components/ConnectUserWallet/ConnectionLoading';
import { WalletSelector } from '@ewf/components/ModalUI/WalletSelector';
import { ConnectLedger } from '@ewf/components/ConnectLedger';
import { useBalanceStore } from '@ewf/stores/balance';

export type Step = 'select' | 'connectWC' | 'connectLedger' | 'loading' | 'error' | 'connected';

export const ConnectETHWallet = ({ onClose }: Props) => {
  const [step, setStep] = useState<Step>('select');
  const [selectedWallet, setSelectedWallet] = useState<Wallet>('wallet-connect');
  const [errMsg, setErrMsg] = useState<string>();
  const stepRef = useRef<Step>(step);
  const isSubscribedModalRef = useRef<boolean>(false);
  const walletConnectModal = useConnectionStore((state) => state.walletConnectModal);
  const errorMsg = useConnectionStore((state) => state.errorMsg);
  const isConnectedETH = useConnectionStore((state) => state.isConnectedETH);
  const addressETH = useConnectionStore((state) => state.addressETH);
  const balanceETH = useBalanceStore((state) => state.balanceETH);
  const disconnect = useConnectionStore((state) => state.disconnect);

  if (errorMsg) {
    console.log('Error msg: ', errorMsg);
  }

  const goToConnectionMethod = (wallet: Wallet) => {
    setSelectedWallet(wallet);
    if (wallet === 'wallet-connect') {
      setStep('connectWC');
    }

    if (wallet === 'ledger') {
      setStep('connectLedger');
    }
  };

  const connectionSuccess = () => {
    setStep('connected');
  };

  const connectionError = (msg?: string) => {
    setErrMsg(msg);
    setStep('error');
  };

  const handleDisconnect = async () => {
    await disconnect(MARKETPLACE_CHAINS.ETH);
    setStep('select');
  };

  useEffect(() => {
    if (!walletConnectModal) return;
    if (isSubscribedModalRef.current) return;
    isSubscribedModalRef.current = true;

    walletConnectModal.subscribeModal((state) => {
      if (!state.open && stepRef.current === 'connectWC') {
        setTimeout(() => {
          if (stepRef.current === 'connectWC') setStep('select');
        }, 300);
      }

      if (!state.open && step === 'connectLedger') {
        setStep('select');
      }
    });
  }, [step, walletConnectModal]);

  useEffect(() => {
    // Ref is used to keep track of step current value inside of setTimeout callback
    stepRef.current = step;
  }, [step]);

  // Check if already connected
  useEffect(() => {
    if (isConnectedETH && step === 'select') {
      setStep('connected');
    }
  }, [isConnectedETH, step]);

  const stepComponents: Record<Step, React.ReactNode> = {
    select: (
      <div className="p-6 bg-popup-gradient rounded-lg">
        <h2 className="text-xl font-bold text-white mb-4">Connect to Ethereum</h2>
        <WalletSelector
          selectedWallet={selectedWallet}
          setSelectedWallet={setSelectedWallet}
          network={MARKETPLACE_CHAINS.ETH}
        />
        <div className="mt-6 flex gap-4">
          <button
            onClick={() => goToConnectionMethod(selectedWallet)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Connect
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Cancel
          </button>
        </div>
      </div>
    ),
    connectWC: (
      <ConnectWC
        chain={MARKETPLACE_CHAINS.ETH}
        isConnectingCallback={() => {
          walletConnectModal?.closeModal();
          setStep('loading');
        }}
        onConnectionSuccess={connectionSuccess}
        onConnectionError={connectionError}
      />
    ),
    connectLedger: (
      <ConnectLedger
        chain={MARKETPLACE_CHAINS.ETH}
        onClose={onClose}
        onConnectionSuccess={connectionSuccess}
        onConnectionError={connectionError}
      />
    ),
    loading: <ConnectionLoading message="Connecting to Ethereum..." />,
    error: (
      <div className="p-6 bg-popup-gradient rounded-lg">
        <h2 className="text-xl font-bold text-red-500 mb-4">Connection Error</h2>
        <p className="text-white mb-4">{errMsg || 'Unknown error occurred'}</p>
        <div className="flex gap-4">
          <button
            onClick={() => setStep('select')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    ),
    connected: (
      <div className="p-6 bg-popup-gradient rounded-lg">
        <h2 className="text-xl font-bold text-green-500 mb-4">✓ Connected to Ethereum</h2>
        <div className="text-white space-y-2">
          <p><strong>Address:</strong> {addressETH}</p>
          <p><strong>Balance:</strong> {balanceETH?.token || '0'} {balanceETH?.symbol || 'ETH'}</p>
        </div>
        <div className="mt-6 flex gap-4">
          <button
            onClick={handleDisconnect}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Disconnect
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    ),
  };

  const renderComponent = () => {
    return stepComponents[step];
  };

  return renderComponent();
};

interface Props {
  onClose: () => void;
}